import { List, Map } from 'immutable';
import { isEmpty } from 'lodash';

import { getOrderedStakeTypeGroupIdsConf } from 'app/lib/matchGridStakes';
import { BACKUP, RESTORE } from 'app/redux/actions/persistence';
import { TRANSFORM_SSR_STATE } from 'app/redux/ssrState';

/**
 * Translate keyed object to an immutable map with numbered keys.
 *
 * @param {object} node – a keyed object to transform
 * @param {function(value:*):*} transformValue – value translator
 * @return {Immutable.Map<number, *>}
 */
function toMapWithNumberedKeys(node, transformValue) {
  return Map(
    Object.keys(node).map((key) => {
      return [parseInt(key), transformValue(node[key])];
    }),
  );
}

const asIs = (value) => value;

const initialState = Map({
  mainLineStakesGrid: Map(),
  stakeTypeGroupBySportId: Map(),
  orderKey: null,
});

/**
 * Build the `mainLineStakesGrid` node with the following structure:
 *
 * > [matchId][lineId][groupId] = stakeId
 *
 * @private
 * @param {Immutable.Map<string, Immutable.Map<string, *>>} entities
 * @return {Immutable.Map<number, Immutable.Map<number, Immutable.Map<number, number>>>}
 */
export function buildMainLineStakesGrid(entities) {
  const matches = entities.get('matches').get('byId');

  return matches.map((match) => match.lines);
}

/**
 * Build main-line `stakeTypeGroupBySportId` node
 *
 * > [sportId] = [stakeTypeGroupId, ...]
 *
 * @param {Immutable.Map<number, Array<number>>} state – current state
 * @param {Immutable.Map<string, Immutable.Map<string, *>>} entities – entities state
 * @param {string} namespace
 * @return {Immutable.Map<number, Array<number>>} updated state
 */
export function updateStakeTypeGroupBySportId(state, entities, namespace) {
  return entities
    .get('sports')
    .get('allIds')
    .reduce((state, sportId) => {
      if (state.has(sportId)) {
        return state;
      }

      const groupIds = // todo: move out accessing to local storage from here!
         getOrderedStakeTypeGroupIdsConf(sportId);

      return state.set(sportId, new List(groupIds));
    }, state);
}

/**
 * @param {object} state – server-side rendered state
 * @return {Immutable.Map<number, Immutable.Map<number, Immutable.Map<number, number>>>}
 */
export function transformMainLineStakesGrid(state) {
  // todo: optimize (redundant function creation inside nested loops)
  return toMapWithNumberedKeys(state, (state) => {
    return toMapWithNumberedKeys(state, (state) => {
      return toMapWithNumberedKeys(state, asIs);
    });
  });
}

/**
 * @param {object} state
 * @param {string} namespace
 * @param {Store} entities
 * @return {Immutable.Map<number, Immutable.List<number>>}
 */
export function transformStakeTypeGroupBySportId(state, namespace, entities) {
  if (!isEmpty(state)) {
    const stakeTypeGroupBySportId = entities
      .get('sports')
      .get('allIds')
      .reduce((state, sportId) => {
        const groupIds = // FIXME
          getOrderedStakeTypeGroupIdsConf(sportId);

        state[sportId] = List(groupIds);

        return state;
      }, state);

    const mappedNumberedKeys = (
      toMapWithNumberedKeys(stakeTypeGroupBySportId, (value) => List(value))
    );

    return Map(mappedNumberedKeys);
  }

  return Map(toMapWithNumberedKeys(state, (value) => List(value)));
}

function handleBackup(state) {
  return {
    mainLineStakesGrid: state.mainLineStakesGrid,
    stakeTypeGroupBySportId: state.stakeTypeGroupBySportId,
    orderKey: state.orderKey,
  };
}

function handleRestore(state, payload) {
  if (payload.mainLineStakesGrid) {
    state = {
      ...state,
      mainLineStakesGrid: payload.mainLineStakesGrid,
    };
  }

  if (payload.stakeTypeGroupBySportId) {
    state = {
      ...state,
      stakeTypeGroupBySportId: payload.stakeGroupTypeIds,
    };
  }

  if (payload.orderKey) {
    state = {
      ...state,
      orderKey: payload.orderKey,
    };
  }

  return state;
}

export default ({
  APPLY_STATE,
  RESET_STATE = null,
  SET_GRID_STAKE_TYPES = null,
  SET_GRID_ORDER_KEY = null,
}) =>
  function (state = initialState, namespace, action, entities) {
    const { type, payload = null } = action;

    switch (type) {
      case BACKUP:
        return handleBackup(state);

      case RESTORE:
        return handleRestore(state, payload);

      case APPLY_STATE:
        if (!isEmpty(payload.diff.matches)) {
          state = state.set('mainLineStakesGrid', buildMainLineStakesGrid(entities));
        }

        if (!isEmpty(payload.diff.sports)) {
          state = state.update('stakeTypeGroupBySportId', (state) => {
            return updateStakeTypeGroupBySportId(state, entities, namespace);
          });
        }

        return state;

      case RESET_STATE:
        return state.merge({
          mainLineStakesGrid: initialState.get('mainLineStakesGrid'),
          stakeTypeGroupBySportId: initialState.get('stakeTypeGroupBySportId'),
        });

      case SET_GRID_STAKE_TYPES:
        // noinspection JSSwitchVariableDeclarationIssue
        return state.update('stakeTypeGroupBySportId', (state) => {
          const { sportId, stakeGroupTypeIds } = payload;
          return state.set(sportId, stakeGroupTypeIds);
        });

      case SET_GRID_ORDER_KEY:
        return state.set('orderKey', payload.key);

      case TRANSFORM_SSR_STATE:
        if (Map.isMap(state)) {
          return state; // already transformed (initial state case)
        }

        return Map({
          mainLineStakesGrid: transformMainLineStakesGrid(state.mainLineStakesGrid),
          stakeTypeGroupBySportId: transformStakeTypeGroupBySportId(
            state.stakeTypeGroupBySportId,
            namespace,
            entities,
          ),
          filterKey: state.filterKey,
          orderKey: state.orderKey,
        });

      default:
        return state;
    }
  };
